import he from 'he';
import { memo, useMemo } from 'react';
import { TextStyle, useWindowDimensions } from 'react-native';
import RenderHTML, {
  CustomRendererProps,
  CustomTagRendererRecord,
  TNode,
} from 'react-native-render-html';
import { Variants } from '../Text/Text.content';
import useStyle from '../../hooks/useStyle';
import { RenderHtmlProps } from './RenderHtml.types';
import { Text } from '../../index';

function RenderHtml({ html, variant, color, onParseError }: RenderHtmlProps) {
  const { width } = useWindowDimensions();

  const defaultTextStyle = useStyle<TextStyle>({
    color: color ?? 'darkSurfacePrimary',
    ...Variants[variant],
  });

  const getTextContent = (tnode: TNode): string => {
    if (tnode.type === 'text') {
      return tnode.data || '';
    }

    if (tnode.children && tnode.children.length > 0) {
      return tnode.children.map((child) => getTextContent(child)).join('');
    }

    return '';
  };

  const decodedHtml = useMemo(() => {
    try {
      const decoded = he.decode(html);

      return decoded.replace(/<p><br><\/p>/g, '');
    } catch (e) {
      onParseError?.(e);

      return '';
    }
  }, [html, onParseError]);

  const renderersProps = useMemo(
    () => ({
      ul: {
        markerTextStyle: { color: defaultTextStyle.color },
      },
      ol: {
        markerTextStyle: { color: defaultTextStyle.color },
      },
    }),
    [defaultTextStyle.color],
  );

  const renderers = {
    h1: ({ tnode }: CustomRendererProps<TNode>) => {
      const textContent = getTextContent(tnode);
      return (
        <Text
          variant="h4"
          marginBottom="space-600"
          gradient="darkSurfaceAccent"
        >
          {textContent}
        </Text>
      );
    },

    h2: ({ tnode }: CustomRendererProps<TNode>) => {
      const textContent = getTextContent(tnode);
      return (
        <Text
          variant="h5"
          marginBottom="space-800"
          gradient="darkSurfaceAccent"
        >
          {textContent}
        </Text>
      );
    },

    h3: ({ tnode }: CustomRendererProps<TNode>) => {
      const textContent = getTextContent(tnode);
      return (
        <Text
          variant="h6"
          marginBottom="space-300"
          marginTop="space-800"
          gradient="darkSurfaceAccent"
        >
          {textContent}
        </Text>
      );
    },

    p: ({ tnode }: CustomRendererProps<TNode>) => {
      const textContent = getTextContent(tnode);
      return (
        <Text
          variant="body-m"
          marginBottom="space-500"
          color="darkSurfacePrimary"
        >
          {textContent}
        </Text>
      );
    },
  } as CustomTagRendererRecord;

  return (
    <RenderHTML
      contentWidth={width}
      defaultTextProps={{
        style: defaultTextStyle as object,
      }}
      renderers={renderers}
      renderersProps={renderersProps}
      source={{ html: decodedHtml }}
      enableExperimentalGhostLinesPrevention
      enableExperimentalBRCollapsing
      enableExperimentalMarginCollapsing
    />
  );
}

export default memo(RenderHtml);
